import React, { useState, useEffect } from 'react';
import ToolFooter from '../ui/ToolFooter';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';


const PropertyDecisionTree = ({ isEmbedded = false }) => {
  const [grossRent, setGrossRent] = useState('');
  const [interestPaid, setInterestPaid] = useState('');
  const [otherExpenses, setOtherExpenses] = useState('');

  const propertyAllowance = 1000;
  const actualExpenses = Number(otherExpenses) || 0;
  
  const strategy = actualExpenses > propertyAllowance ? "ACTUAL_EXPENSES" : "PROPERTY_ALLOWANCE";
  const savings = Math.abs(actualExpenses - propertyAllowance);

  useEffect(() => {
    if (grossRent || otherExpenses || interestPaid) {
      const timer = setTimeout(() => {
        trackEvent('tool_completion', { tool_name: 'property_allowance' });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [grossRent, otherExpenses, interestPaid]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Track Property Expenses via WhatsApp",
    "dateModified": getFreshnessDateIso(),
    "step": [
      {"@type": "HowToStep", "text": "Compare your actual expenses to the flat £1,000 allowance."},
      {"@type": "HowToStep", "text": "If expenses are higher, photograph your repair & insurance receipts in WhatsApp."},
      {"@type": "HowToStep", "text": "Zap Expenses extracts suggested categories for review, then you export structured records for your accountant or filing software."}
    ]
  };

  return (
    <div className="w-full max-w-2xl mx-auto my-12">
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl">

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <FreshnessDate className="mb-4" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-4">Property Allowance vs. Actual Expenses Calculator</h1>
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>If your total landlord repair, maintenance, and insurance costs exceed £1,000 in a tax year, you should claim "Actual Expenses" instead of the Property Income Allowance to lower your tax bill.</strong>
          </p>
        </div>
        
        <div className="prose prose-invert max-w-none text-slate-300 mb-8">
          <p>
            Tracking "Actual Expenses" usually requires meticulous record-keeping, which is why many landlords under-claim. Under <strong>Making Tax Digital for Income Tax</strong> (see <strong>HMRC</strong> manual PIM2010), digital records belong in compatible software. Snap a photo of your receipt in <strong>WhatsApp</strong> and Zap Expenses extracts suggested property categories for review — not tax advice, and not a substitute for filing software.
          </p>
        </div>
        
        <p className="text-slate-400 text-sm font-semibold">Enter your property financials below to find your recommended tax path.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Gross Rent (£)</label>
          <input
            type="number"
            value={grossRent}
            onChange={(e) => setGrossRent(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
            placeholder="e.g. 12000"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Other Expenses (£)</label>
          <input
            type="number"
            value={otherExpenses}
            onChange={(e) => setOtherExpenses(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
            placeholder="Repairs, insurance, etc."
          />
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Mortgage Interest (£)</label>
          <input
            type="number"
            value={interestPaid}
            onChange={(e) => setInterestPaid(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono"
            placeholder="e.g. 5000"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* The Easy Path */}
        <div className={`p-6 rounded-2xl border transition-all duration-300 ${strategy === 'PROPERTY_ALLOWANCE' ? 'bg-emerald-500/10 border-emerald-500 shadow-lg shadow-emerald-500/10' : 'bg-black/20 border-white/5 opacity-60'}`}>
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-lg font-bold text-white">The Easy Path<br/><span className="text-sm font-normal text-slate-400">£1,000 flat property allowance</span></h4>
            {strategy === 'PROPERTY_ALLOWANCE' && <span className="bg-emerald-500 text-white text-[10px] font-bold uppercase px-2 py-1 rounded">Recommended</span>}
          </div>
          <div className="text-3xl font-mono font-bold text-emerald-400 mb-2">Claim £1,000</div>
          <p className="text-xs text-slate-400 leading-relaxed">No need to track repair receipts or insurance premiums. Simply deduct £1,000 from your gross rent.</p>
        </div>

        {/* The Pro Path */}
        <div className={`p-6 rounded-2xl border transition-all duration-300 ${strategy === 'ACTUAL_EXPENSES' ? 'bg-indigo-500/10 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-black/20 border-white/5 opacity-60'}`}>
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-lg font-bold text-white">The Pro Path<br/><span className="text-sm font-normal text-slate-400">Detailed Tracking</span></h4>
            {strategy === 'ACTUAL_EXPENSES' && <span className="bg-indigo-500 text-white text-[10px] font-bold uppercase px-2 py-1 rounded">Recommended</span>}
          </div>
          <div className="text-3xl font-mono font-bold text-indigo-400 mb-2">Claim £{actualExpenses.toLocaleString()}</div>
          <p className="text-xs text-slate-400 leading-relaxed">Track every expense. Since actual costs exceed £1,000, tracking receipts is worth the effort.</p>
        </div>
      </div>

      {otherExpenses && (
        <div className="bg-indigo-900/40 border border-indigo-500/30 p-4 rounded-xl flex items-start gap-3">
          <span className="text-indigo-400 mt-0.5">💡</span>
          <div className="text-sm text-indigo-100">
            <strong>Outcome:</strong> By choosing {strategy === 'ACTUAL_EXPENSES' ? 'Actual Expenses' : 'the Property Allowance'}, you can claim <strong className="text-white">£{savings.toLocaleString()}</strong> more against your taxes.
            {interestPaid && (
              <div className="mt-2 text-indigo-300 text-xs italic">
              </div>
            )}
          </div>
        </div>
      )}

      <div id="stat-leakage-2026" className="mt-10 mb-2 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden text-center md:text-left">
        <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">Expense Tracking Friction</p>
        <p className="text-2xl font-extrabold text-white mb-2">The £5.9bn Physical Receipt Trap</p>
        <p className="text-slate-400 text-sm">
          Physical receipt loss accounts for £5.9bn of the UK small business tax gap due to error or lack of evidence, leading landlords relying on shoebox methods to miss legitimate allowances.
        </p>
        <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2 text-center md:text-left">
          Source: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC, Tax gap by component: Small Business (2024/25 Update)</a>
        </p>
      </div>

      <div className="mt-6 mb-2 text-center">
        <a href="https://www.gov.uk/guidance/income-tax-when-you-rent-out-a-property-working-out-your-rental-income" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
          Verify Property Income Allowances on Gov.uk →
        </a>
      </div>

      <ToolFooter />
        </div>
    </div>
  );
};

export default PropertyDecisionTree;
